import React, { FC, useState, FormEvent, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../auth/authActions";
import axios from "axios";
import { startLoading, endLoading } from "../state/appActions";
import { Dispatch } from "redux";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser, getGoogleLoginURL, login as loginPost } from "../api/helpers";
import { GetUser } from "../types";
import { setUserData } from "../auth/authActions";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { ArrowLeft } from "lucide-react";
import "./styles/globals.css";

const loginAsync = async (
  username: string,
  password: string,
  dispatch: Dispatch
) => {
  const data = {
    username,
    password,
  };
  const response = await loginPost(data);
  if (axios.isAxiosError(response)) {
    return { success: false, message: response?.response?.data?.message };
  } else {
    if (response?.data?.status === "success") {
      dispatch(login());
      let data: GetUser = await getUser(dispatch);
      dispatch(setUserData(data.user));
      return { success: true };
    }
    return { success: false };
  }
};

const OrgEqLogin: FC = () => {
  const location = useLocation();
  let dispatch = useDispatch();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const queryParams = new URLSearchParams(location.search);
  const redirect = queryParams.get("redirect");

  const isAuthenticated = useSelector(
    (state: any) => state.auth.isAuthenticated
  );

  useEffect(() => {
    // Only redirect if authenticated AND not coming from a sign-out action
    // Check if we're explicitly trying to view the login page
    const params = new URLSearchParams(location.search);
    const forceLogin = params.get('force');
    
    if (isAuthenticated && !forceLogin) {
      navigate("/org-eq#dashboard");
    }
  }, [isAuthenticated, navigate, location.search]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(startLoading());
    setError("");
    let response = await loginAsync(username, password, dispatch);
    if (!response.success) {
      if (response.message) {
        setError(response.message);
      } else {
        setError("Login failed. Please check your credentials and try again.");
      }
    }
    dispatch(endLoading());
    if (response.success) {
      if (redirect) {
        navigate(redirect);
      } else {
        navigate("/org-eq#dashboard");
      }
    }
  };

  const handleGoogleSignIn = async () => {
    dispatch(startLoading());
    // Set flag to indicate OrgEQ login
    localStorage.setItem('orgEqLogin', 'true');
    const response = await getGoogleLoginURL();
    if (response) {
      window.location.assign(response.redirect_url);
    } else {
      dispatch(endLoading());
      localStorage.removeItem('orgEqLogin');
      setError("Error loading Google authenticator. Please refresh and try again");
    }
  };

  return (
    <div className="org-eq-dashboard min-h-screen bg-background flex items-center justify-center p-4">
      <button
        onClick={() => navigate("/org-eq/home")}
        className="fixed top-6 left-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Return to homepage
      </button>

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4 text-center">
          <img 
            src="/logos/orgeq-logo.svg" 
            alt="OrgEQ" 
            className="w-[196px] h-auto mx-auto"
          />
          <CardTitle className="text-2xl">Sign In</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <Button type="submit" className="w-full">
              Sign In
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <Button 
            variant="outline" 
            className="w-full gap-2 bg-transparent opacity-50 cursor-not-allowed" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            disabled
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrgEqLogin;
